import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import {
  onExpandEvent,
  removeHighlightByEvent,
} from '@store/timeline/ui/actions';
import {
  SESSION_TYPES,
  EXPANDABLE_EVENTS,
  TIMELINE_DIMENSION,
  ALL_DISPLAY_NAMES,
} from '@components/session-timeline/utils/constants';
import { highlightsSelector } from '@store/timeline/ui/selectors';
import { timelineDataSelector } from '@store/timeline/data/selectors';
import { CHART_TYPE } from './TimelineChart/enums';
import { Wrapper, Main } from './styled';
import { TimelineEventProps } from './types';
import SessionOverview from '../SessionOverview';
import TimelineChart from './TimelineChart';
import ExpandIconButton from './ExpandIconButton';
import SessionTable from './SessionTable';

const eventCount = (timeline, label, dateRange) => {
  const data = get(timeline, [label], []);
  const min = dateRange[0];
  const max = dateRange[dateRange.length - 1];
  const eventsInRange = data.filter(event => {
    if (event.timeMs && min <= event.timeMs && event.timeMs <= max) {
      return true;
    }
    if (event.startTimeMs && event.endTimeMs) {
      const start = Math.max(event.startTimeMs, min);
      const end = Math.min(event.endTimeMs, max);
      if (start < end) {
        return true;
      }
    }
    return false;
  })
  return eventsInRange.length > 0 ? `(${eventsInRange.length})` : '';
}

const TimelineEvent = ({
  type = SESSION_TYPES.NORMAL,
  label,
  expand = false,
  divided,
  lightBorder,
  dateRange,
  noDataText,
}: TimelineEventProps) => {
  const timelineData = useSelector(timelineDataSelector);
  const expandable = EXPANDABLE_EVENTS.includes(label);
  const dispatch = useDispatch();
  const highlightMoment = get(useSelector(highlightsSelector), [label], null);
  const isFetching = true;
  const handleExpandIconClick = useCallback(() => {
    dispatch(onExpandEvent(label));
    if (highlightMoment) {
      dispatch(removeHighlightByEvent(label));
    }
  }, [label, dispatch, highlightMoment]);

  const Chart = useMemo(() => {
    return (
      <>
        {([
          TIMELINE_DIMENSION.Bitrate,
          TIMELINE_DIMENSION.Framerate,
        ] as string[]).includes(label) && (
          <TimelineChart
            type={CHART_TYPE.LINE}
            event={label}
            highlightMoment={highlightMoment}
            dateRange={dateRange}
          />
        )}
        {([
          TIMELINE_DIMENSION.CDN,
          TIMELINE_DIMENSION.Asset,
        ] as string[]).includes(label) && (
          <TimelineChart
            type={CHART_TYPE.STATE}
            event={label}
            highlightMoment={highlightMoment}
            dateRange={dateRange}
          />
        )}
        {([
          TIMELINE_DIMENSION.Error,
          TIMELINE_DIMENSION.CustomEvent
        ] as string[]).includes(label) && (
          <TimelineChart
            type={CHART_TYPE.EVENT}
            event={label}
            highlightMoment={highlightMoment}
            dateRange={dateRange}
          />
        )}
      </>
    );
  }, [isFetching, label, highlightMoment, dateRange]);

  const sessionOverview = useMemo(() => {
    return <SessionOverview dateRange={dateRange} />;
  }, [isFetching, dateRange]);

  const sessionTable = useMemo(() => {
    return (
      <>
        <SessionTable
          event={label}
          expand={expand}
          highlightMoment={highlightMoment}
          dateRange={dateRange}
          noDataText={noDataText}
        />
      </>
    );
  }, [isFetching, label, expand, highlightMoment, dateRange]);

  return (
    <Wrapper>
      <Main
        className={classnames({
          'session-overview': type === SESSION_TYPES.SESSION_OVERVIEW,
        })}
        type={type}
        divided={divided}
        lightBorder={lightBorder}
      >
        <div className="left">
          {expandable && (
            <ExpandIconButton
              expand={expand}
              onClick={handleExpandIconClick}
            />
          )}
          <label className="label">{ALL_DISPLAY_NAMES[label] || label} {`${eventCount(timelineData, label, dateRange)}`}</label>
        </div>
        <div className="right">
          {type === SESSION_TYPES.SESSION_OVERVIEW ? sessionOverview : null}
          {(type !== SESSION_TYPES.SESSION_OVERVIEW) ? Chart : null}
        </div>
      </Main>
      {expandable && sessionTable}
    </Wrapper>
  );
};

export default TimelineEvent;
